import { useInfiniteQuery } from '@tanstack/react-query';
import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services/ApiClient';
import { ViewPreInvoicesApiAdapter } from '../adapters/ViewPreInvoicesApiAdapter';
import { PreInvoiceStorage } from '../models/ViewPreInvoices';

interface UseViewPreInvoiceStoragesProps {
  preInvoiceId: number;
  searchFilter?: { filters: any[]; logic: string };
  search?: string;
  sortBy?: string;
  sortDirection?: 'asc' | 'desc';
  enabled?: boolean;
}

/**
 * useViewPreInvoiceStorages - Hook para cargar storages de una prefactura con paginación infinita
 * 
 * Utiliza React Query (useInfiniteQuery) para manejar la carga paginada de storages
 * desde el endpoint /invoices/pre-invoices/{id}/storages con soporte para:
 * - Infinite scroll
 * - Filtros avanzados
 * - Sorting
 * - Cache automático
 * 
 * @param {UseViewPreInvoiceStoragesProps} props - Configuración de filtros y sorting
 * @returns {Object} Estado de la query con datos, loading, error y funciones de paginación
 */
export const useViewPreInvoiceStorages = ({
  preInvoiceId,
  searchFilter,
  search,
  sortBy = 'product_id',
  sortDirection = 'asc',
  enabled = true,
}: UseViewPreInvoiceStoragesProps) => {
  const preInvoiceNumericId = Number(preInvoiceId);
  const isEnabled = enabled && Number.isFinite(preInvoiceNumericId) && preInvoiceNumericId > 0;

  const httpClient = createHttpClient(apiClientWH);
  const viewPreInvoicesAdapter = new ViewPreInvoicesApiAdapter(httpClient);

  const {
    data,
    isLoading,
    isFetching,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ['viewPreInvoiceStorages', preInvoiceNumericId, searchFilter, search, sortBy, sortDirection],
    queryFn: ({ pageParam = 1 }) =>
      viewPreInvoicesAdapter.getPreInvoiceStoragesPaginated({
        preInvoiceId: preInvoiceNumericId,
        searchFilter,
        search,
        sortBy,
        sortDirection,
        page: pageParam,
        limit: 25,
      }),
    getNextPageParam: (lastPage) => lastPage.nextPage,
    initialPageParam: 1,
    staleTime: 2 * 60 * 1000, // 2 minutos
    gcTime: 5 * 60 * 1000, // 5 minutos
    enabled: isEnabled,
    placeholderData: (previousData) => previousData,
  });

  // Aplanar todas las páginas en un solo array
  const storages: PreInvoiceStorage[] =
    data?.pages.flatMap((page) => page.storages) ?? [];

  // Total de resultados (de la primera página)
  const total = data?.pages[0]?.total ?? 0;

  return {
    storages,
    isLoading,
    isFetching,
    error: error ? (error as Error).message : null,
    total,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  };
};

